/**
 * JobMockAI — App.js (Phase 3 UI Complete)
 *
 * Full SPA router powered by react-router-dom v6.
 * Authenticated pages render inside AppShell (sidebar + top bar).
 * Admin pages render inside AdminLayout with dedicated sidebar.
 * Public pages (landing, auth, oauth-callback) render without the shell.
 * All page components are lazy-loaded with Suspense + PageLoader fallback.
 */
import React, { useMemo, useState, useEffect, lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Psychology as PsychologyIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import DemoStudioRoute from './components/DemoStudioRoute';
import DemoBookingRedirect from './components/DemoBookingRedirect';
import { isDemoStudioEnabled } from './utils/demoStudioAccess';
import AppShell from './layouts/AppShell';
import JobsDiscoveryLayout from './layouts/JobsDiscoveryLayout';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import InterviewQuotaBanner from './components/InterviewQuotaBanner';
import api from './api/client';
import { resolveAuthenticatedSignupRedirect } from './utils/authRedirect';
import { resolveRedirectWithFeatureFlags } from './utils/postAuthNavigation';
import { consumeOAuthPostAuthRedirect } from './utils/oauthPostAuthRedirect';
import {
  consumeSignupPostAuthRedirect,
  peekSignupPostAuthRedirect,
  clearSignupPostAuthRedirect,
} from './utils/signupAuthRedirect';
import './App.css';

// ── Eager imports (critical path — needed immediately) ───────────
import LandingPage from './components/LandingPage';
import AuthWrapper from './components/AuthWrapper';
import AuthenticatedAuthRedirect from './components/AuthenticatedAuthRedirect';
import OAuthCallback from './components/OAuthCallback';
import UserDashboard from './components/Dashboard';

// ── Lazy imports (code-split — loaded on demand) ─────────────────
const LearningHub = lazy(() => import('./pages/LearningHub'));
const LearningPath = lazy(() => import('./pages/LearningPath'));
const LearningResource = lazy(() => import('./pages/LearningResource'));
const ProgressDashboard = lazy(() => import('./pages/ProgressDashboard'));
const CareerGrowth = lazy(() => import('./pages/CareerGrowth'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const InterviewRoom = lazy(() => import('./pages/InterviewRoom'));
const RoomLobby = lazy(() => import('./pages/RoomLobby'));
const RoomJoin = lazy(() => import('./pages/RoomJoin'));
const ScheduleInterview = lazy(() => import('./pages/ScheduleInterview'));
const MySchedules = lazy(() => import('./pages/MySchedules'));
const MyObservations = lazy(() => import('./pages/MyObservations'));
const InterviewReplay = lazy(() => import('./pages/InterviewReplay'));
const ReferralDashboard = lazy(() => import('./pages/ReferralDashboard'));
const MarketplacePage = lazy(() => import('./pages/MarketplacePage'));
const AICoach = lazy(() => import('./pages/AICoach'));
const AgentsHubPage = lazy(() => import('./pages/agents/AgentsHubPage'));
const AgentWizardPage = lazy(() => import('./pages/agents/AgentWizardPage'));
const AgentChatPage = lazy(() => import('./pages/agents/AgentChatPage'));
const AgentMissionDashboard = lazy(() => import('./pages/agents/AgentMissionDashboard'));
const AgentOnboardingPage = lazy(() => import('./pages/agents/AgentOnboardingPage'));
const ReadinessReportPage = lazy(() => import('./pages/agents/ReadinessReportPage'));
const HiringMarketplace = lazy(() => import('./pages/HiringMarketplace'));
const JobBoard = lazy(() => import('./pages/JobBoard'));
const PublicJobBoard = lazy(() => import('./pages/hiring/PublicJobBoard.jsx'));
const InterviewPrepHub = lazy(() => import('./pages/hiring/InterviewPrepHub.jsx'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const CompanyPublicPage = lazy(() => import('./pages/CompanyPublicPage'));
const InviteAcceptPage = lazy(() => import('./pages/InviteAcceptPage'));
const MyApplications = lazy(() => import('./pages/MyApplications'));
const CandidateVisibility = lazy(() => import('./pages/CandidateVisibility'));
const CompanyDashboard = lazy(() => import('./pages/CompanyDashboard'));
const InterviewTemplateBuilderPage = lazy(() => import('./pages/InterviewTemplateBuilderPage'));
const AssignedAiInterviewPage = lazy(() => import('./pages/AssignedAiInterviewPage'));
const BookingPrepInterviewPage = lazy(() => import('./pages/BookingPrepInterviewPage'));
const ClaimApplicationPage = lazy(() => import('./pages/ClaimApplicationPage'));
const PracticeInterviewsPage = lazy(() => import('./pages/PracticeInterviewsPage'));
const ShareInterviewExperiencePage = lazy(() => import('./pages/ShareInterviewExperiencePage'));
const PracticeInterviewDetailPage = lazy(() => import('./pages/PracticeInterviewDetailPage'));
const ObservationReport = lazy(() => import('./components/ai-observer/ObservationReport'));
const Billing = lazy(() => import('./components/Billing'));
const PaymentReturn = lazy(() => import('./components/PaymentReturn'));
const EnhancedPricingPage = lazy(() => import('./components/EnhancedPricingPage'));
const PublicPricingPage = lazy(() => import('./pages/PublicPricingPage'));
const CandidatePage = lazy(() => import('./pages/CandidatePage'));
const ProductToursPage = lazy(() => import('./pages/ProductToursPage'));
const MarketingStaticPage = lazy(() => import('./pages/MarketingStaticPage'));
const AboutPage = lazy(() => import('./components/AboutUs'));
const CareersPage = lazy(() => import('./components/Careers'));
const FeaturesPage = lazy(() => import('./components/Features'));
const ApiOverviewPage = lazy(() => import('./components/API'));
const IntegrationsPage = lazy(() => import('./components/Integrations'));
const HelpCenterPage = lazy(() => import('./components/HelpCenter'));
const ContactUsPage = lazy(() => import('./components/ContactUs'));
const StatusPage = lazy(() => import('./components/Status'));
const CommunityMarketingPage = lazy(() => import('./components/Community'));
const CookiePolicyPage = lazy(() => import('./components/CookiePolicy'));
const GdprPage = lazy(() => import('./components/GDPRCompliance'));
const EnterpriseCandidateInsights = lazy(() => import('./components/EnterpriseCandidateInsights'));
const CustomInterviewBuilderPage = lazy(() => import('./components/CustomInterviewBuilderPage'));
const VoiceCoachPage = lazy(() => import('./pages/VoiceCoachPage'));
const Reviews = lazy(() => import('./components/Reviews'));
const TTSDebugger = lazy(() => import('./components/TTSDebugger'));

// Feedback & Reviews pages (Phase 4 Step 7)
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const ReviewsPage = lazy(() => import('./pages/ReviewsPage'));

// Auth completion pages (Step 36)
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));

// Profile & Settings pages (Step 37)
const UserProfile = lazy(() => import('./pages/UserProfile'));
const SettingsPage = lazy(() => import('./pages/Settings'));

// Interview experience pages (Step 38)
const InterviewHistory = lazy(() => import('./pages/InterviewHistory'));
const SharedInterview = lazy(() => import('./pages/SharedInterview'));

// Community pages (Step 39)
const PeopleDiscovery = lazy(() => import('./pages/PeopleDiscovery'));
const PublicProfile = lazy(() => import('./pages/PublicProfile'));
const MyConnections = lazy(() => import('./pages/MyConnections'));
const EditCommunityProfile = lazy(() => import('./pages/EditCommunityProfile'));
const CommunityFeed = lazy(() => import('./pages/CommunityFeed'));
const CommunityMessages = lazy(() => import('./pages/CommunityMessages'));

// Billing pages (Step 40)
const BillingPage = lazy(() => import('./pages/BillingPage'));

// Notifications page (Step 41)
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));

// XP & Achievements pages (Step 42)
const Achievements = lazy(() => import('./pages/Achievements'));
const GamesHubPage = lazy(() => import('./pages/GamesHubPage'));
const DailySudokuPage = lazy(() => import('./pages/DailySudokuPage'));
const QueensGamePage = lazy(() => import('./pages/QueensGamePage'));
const BinaryGridGamePage = lazy(() => import('./pages/BinaryGridGamePage'));
const DailyPuzzlePage = lazy(() => import('./pages/DailyPuzzlePage'));
const XPHistory = lazy(() => import('./pages/XPHistory'));

// Admin pages (Step 43)
const AdminLayout = lazy(() => import('./layouts/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const FeatureFlagManager = lazy(() => import('./pages/admin/FeatureFlagManager'));
const PlanQuotasManager = lazy(() => import('./pages/admin/PlanQuotasManager'));
const AuditLogViewer = lazy(() => import('./pages/admin/AuditLogViewer'));
const AIUsageDashboard = lazy(() => import('./pages/admin/AIUsageDashboard'));
const CompanyManagement = lazy(() => import('./pages/admin/CompanyManagement'));
const AdminJobModerationPage = lazy(() => import('./pages/admin/AdminJobModerationPage'));
const AdminPracticeInterviewModerationPage = lazy(() =>
  import('./pages/admin/AdminPracticeInterviewModerationPage')
);
const AdminStudyCatalogPage = lazy(() =>
  import('./pages/admin/AdminStudyCatalogPage')
);
const PlacementPrepHubPage = lazy(() => import('./pages/PlacementPrepHubPage'));
const AptitudeQuizPage = lazy(() => import('./pages/AptitudeQuizPage'));
const CampusDrillPage = lazy(() => import('./pages/CampusDrillPage'));
const CampusTpoPage = lazy(() => import('./pages/CampusTpoPage'));
const AdminInterviewerPage = lazy(() => import('./pages/admin/AdminInterviewerPage'));
const AdminInterviewerApplicationsPage = lazy(() =>
  import('./pages/admin/AdminInterviewerApplicationsPage')
);
const BecomeInterviewerPage = lazy(() => import('./pages/BecomeInterviewerPage'));
const AdminFoundingInterviewerApplicationsPage = lazy(() =>
  import('./pages/admin/AdminFoundingInterviewerApplicationsPage')
);
const AdminListingReviewPage = lazy(() =>
  import('./pages/admin/AdminListingReviewPage')
);
const AdminMediaModerationPage = lazy(() =>
  import('./pages/admin/AdminMediaModerationPage')
);
const AdminVideoLimitsPage = lazy(() => import('./pages/admin/AdminVideoLimitsPage'));
const AdminPayoutManagementPage = lazy(() =>
  import('./pages/admin/AdminPayoutManagementPage')
);
const AdminLegalPage = lazy(() => import('./pages/admin/AdminLegalPage'));
const DemoVideoStudio = lazy(() => import('./pages/studio/DemoVideoStudio'));
const ConsentPage = lazy(() => import('./pages/ConsentPage'));
const PublicLegalPage = lazy(() => import('./pages/legal/PublicLegalPage'));

// Interviewer self-service dashboard
const InterviewerDashboard = lazy(() => import('./pages/InterviewerDashboard'));
const ApplyInterviewer = lazy(() => import('./pages/ApplyInterviewer'));

// Resume management pages (Phase 4 Step 3)
const ResumeManager = lazy(() => import('./pages/ResumeManager'));
const ResumeAnalyzer = lazy(() => import('./pages/ResumeAnalyzer'));

// Marketplace pages (Phase 4 Step 5)
const ServiceDetailPage = lazy(() => import('./pages/ServiceDetailPage'));
const MarketplaceBookings = lazy(() => import('./pages/MarketplaceBookings'));
const LocalRecordingsPage = lazy(() => import('./pages/LocalRecordingsPage'));
const ProviderProfilePage = lazy(() => import('./pages/ProviderProfilePage'));

// Referrals & Rewards (Phase 4 Step 6)
const RewardsStore = lazy(() => import('./pages/RewardsStore'));

// Public SEO content (blog, interview-questions, guides)
const ContentListPage = lazy(() => import('./pages/content/ContentListPage'));
const ContentDetailPage = lazy(() => import('./pages/content/ContentDetailPage'));
const InterviewQuestionsHubPage = lazy(() => import('./pages/content/InterviewQuestionsHubPage'));

// Interview flow components (used by InterviewFlow)
const RoleSelection = lazy(() => import('./components/RoleSelection'));
const ResumeUpload = lazy(() => import('./components/ResumeUpload'));
const InterviewSetup = lazy(() => import('./components/InterviewSetup'));
const VoiceInterview = lazy(() => import('./components/VoiceInterview'));
const InterviewResults = lazy(() => import('./components/InterviewResults'));

// ── Loading fallback ─────────────────────────────────────────────
const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
    <CircularProgress size={40} />
  </Box>
);

// ── Hash Redirect: backward compat for old #/path links ─────────
const HashRedirect = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash && location.hash.startsWith('#/')) {
      const newPath = location.hash.slice(1); // remove the '#'
      window.location.replace(newPath);
    }
  }, [location]);
  return null;
};

// ── Root URL: guests → /landing, signed-in users → /dashboard ──
const HomeRedirect = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/landing" replace />;
};

// ── Landing Wrapper: redirect authenticated users to /dashboard ──
const LandingWrapper = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LandingPage />;
};

// ── Auth Wrapper Page ────────────────────────────────────────────
const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, loading } = useAuth();

  const navigateAfterAuth = React.useCallback(async (userData) => {
    if (!userData) {
      navigate('/interview/practice');
      return;
    }
    const target = resolveAuthenticatedSignupRedirect(location.search, userData)
      || peekSignupPostAuthRedirect()
      || '/dashboard';
    const { path } = await resolveRedirectWithFeatureFlags(target);
    consumeSignupPostAuthRedirect();
    clearSignupPostAuthRedirect();
    navigate(path, { replace: true });
  }, [location.search, navigate]);

  if (loading) {
    return <PageLoader />;
  }

  if (isAuthenticated && user) {
    return <AuthenticatedAuthRedirect search={location.search} user={user} />;
  }

  return (
    <AuthWrapper
      onAuthSuccess={(userData) => {
        if (userData) {
          navigateAfterAuth(userData);
        } else {
          navigate('/interview/practice');
        }
      }}
      onBack={() => navigate('/landing')}
    />
  );
};

// ── OAuth Callback Page ──────────────────────────────────────────
const OAuthCallbackPage = () => {
  const navigate = useNavigate();
  const handleOAuthSuccess = React.useCallback(async (capturedDestination) => {
    consumeOAuthPostAuthRedirect();
    consumeSignupPostAuthRedirect();
    const target = capturedDestination || '/dashboard';
    const { path } = await resolveRedirectWithFeatureFlags(target);
    clearSignupPostAuthRedirect();
    navigate(path, { replace: true });
  }, [navigate]);

  return (
    <OAuthCallback
      onSuccess={handleOAuthSuccess}
      onError={() => navigate('/login')}
    />
  );
};

// ── Observation Report Wrapper (extracts roomId from URL) ────────
const ObservationReportPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  return (
    <Box sx={{ p: 2 }}>
      <Button onClick={() => navigate('/')} sx={{ mb: 2 }}>← Back</Button>
      <Suspense fallback={<PageLoader />}>
        <ObservationReport roomId={roomId} />
      </Suspense>
    </Box>
  );
};

// ── Schedule Interview Wrapper (extracts connectionId / peerId) ──
const ScheduleInterviewPage = () => {
  const { connectionId } = useParams();
  const navigate = useNavigate();
  return (
    <Suspense fallback={<PageLoader />}>
      <ScheduleInterview
        connectionId={connectionId}
        peerId={connectionId}
        onBack={() => navigate('/schedules')}
      />
    </Suspense>
  );
};

// ── Replay Wrapper (extracts sessionId) ──────────────────────────
const ReplayPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  return (
    <Suspense fallback={<PageLoader />}>
      <InterviewReplay
        sessionId={sessionId}
        onBack={() => navigate('/')}
      />
    </Suspense>
  );
};

// ── My Observations Wrapper ──────────────────────────────────────
const MyObservationsPage = () => {
  const navigate = useNavigate();
  return (
    <Suspense fallback={<PageLoader />}>
      <MyObservations
        onBack={() => navigate('/')}
        onViewReport={(roomId) => navigate(`/observation-report/${roomId}`)}
      />
    </Suspense>
  );
};

// ── My Schedules Wrapper ─────────────────────────────────────────
const MySchedulesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <Suspense fallback={<PageLoader />}>
      <MySchedules
        onBack={() => navigate('/')}
        currentUserId={user?.id}
      />
    </Suspense>
  );
};

// ── Dashboard Wrapper: passes useNavigate-based callbacks ────────
const DashboardPage = () => {
  const navigate = useNavigate();
  const { hasSubscription } = useAuth();

  return (
    <UserDashboard
      onStartInterview={() => navigate('/interview/practice')}
      onViewReviews={() => navigate('/reviews')}
      onLogout={() => {}} // logout is now handled by UserMenu
      onPlanInterview={() => {
        if (hasSubscription && hasSubscription('enterprise')) {
          navigate('/interview/custom-builder');
        } else {
          navigate('/pricing');
        }
      }}
      onViewEnterpriseInsights={() => navigate('/enterprise-insights')}
      onNavigateToLearningHub={() => navigate('/learning-hub')}
      onNavigateToProgress={() => navigate('/progress')}
      onNavigateToCareerGrowth={() => navigate('/career-plan')}
      onNavigateToLeaderboard={() => navigate('/leaderboard')}
      onNavigateToRooms={() => navigate('/rooms')}
      onNavigateToSchedules={() => navigate('/schedules')}
      onNavigateToObservations={() => navigate('/ai-observations')}
      onNavigateToReplay={(sessionId) => navigate(`/replay/${sessionId}`)}
      onNavigateToReferrals={() => navigate('/referrals')}
      onNavigateToMarketplace={() => navigate('/marketplace')}
      onNavigateToCoach={() => navigate('/coach')}
      onNavigateToHiring={() => navigate('/hiring')}
      onNavigateToResume={() => navigate('/resumes')}
      onNavigateToDiscover={() => navigate('/community/discover')}
      onNavigateToFeed={() => navigate('/community/feed')}
      onStartSavedPlan={(planConfig) => {
        // Pass plan config via state to the interview flow
        navigate('/interview/practice', { state: { planConfig, startStep: 3 } });
      }}
    />
  );
};

// ═════════════════════════════════════════════════════════════════
// Interview Flow — Extracted stepper-based interview experience
// ═════════════════════════════════════════════════════════════════
const steps = ['Choose Role & Company', 'Resume Options', 'Final Setup', 'Practice Interview', 'Review Results'];
const mobileSteps = ['Role & Company', 'Resume', 'Setup', 'Interview', 'Results'];

const InterviewFlow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const prefillJobId = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      return params.get('prefillJobId') || '';
    } catch {
      return '';
    }
  }, [location.search]);

  // Accept plan, assigned-interview, practice-catalog, or campus placement config from navigate state
  const planConfig = location.state?.planConfig;
  const assignmentConfig = location.state?.assignmentConfig;
  const practiceCatalogConfig = location.state?.practiceCatalogConfig;
  const campusConfig = location.state?.campusConfig;
  const startStep = location.state?.startStep;

  const [activeStep, setActiveStep] = useState(startStep || 0);
  const [resumeData, setResumeData] = useState(null);
  const [interviewConfig, setInterviewConfig] = useState(() => {
    const base = {
      role: 'Software Engineer',
      company: 'Google',
      difficulty: 'medium',
      duration: 15,
      useInterviewer: true,
      jobDescription: '',
    };
    if (practiceCatalogConfig) {
      return {
        ...base,
        mode: 'practice_catalog',
        templateId: practiceCatalogConfig.templateId,
        sessionId: practiceCatalogConfig.sessionId,
        initialQuestion: practiceCatalogConfig.question,
        ttsEnabled: practiceCatalogConfig.ttsEnabled !== false,
        duration: practiceCatalogConfig.duration || base.duration,
        role: practiceCatalogConfig.role || base.role,
        company: practiceCatalogConfig.company || base.company,
      };
    }
    if (assignmentConfig) {
      return {
        ...base,
        mode: 'assigned',
        assignmentId: assignmentConfig.assignmentId,
        sessionId: assignmentConfig.sessionId,
        initialQuestion: assignmentConfig.question,
        ttsEnabled: assignmentConfig.ttsEnabled !== false,
        duration: assignmentConfig.duration || base.duration,
        role: assignmentConfig.role || base.role,
        company: assignmentConfig.company || base.company,
      };
    }
    if (planConfig) {
      return {
        ...base,
        mode: 'planned',
        planId: planConfig.planId,
        sessionId: planConfig.sessionId,
        initialQuestion: planConfig.question,
        ttsEnabled: planConfig.ttsEnabled !== false,
        duration: planConfig.duration || base.duration,
        role: planConfig.role || base.role,
        company: planConfig.company || base.company,
      };
    }
    if (campusConfig) {
      return {
        ...base,
        mode: 'campus',
        type: campusConfig.type || base.type,
        role: campusConfig.role || 'Software Engineer (Campus)',
        company: campusConfig.company || 'Campus Placement',
        difficulty: campusConfig.difficulty || 'easy',
        duration: campusConfig.duration || 20,
        focusAreas: campusConfig.focusAreas || [],
        seniority: campusConfig.seniority || 'Fresher / Campus',
        jobDescription: campusConfig.jobDescription || '',
        campus_track: campusConfig.campus_track || campusConfig.track,
        campus_branch: campusConfig.campus_branch || campusConfig.campusBranch,
        campusConfig: {
          track: campusConfig.campus_track || campusConfig.track,
          branch: campusConfig.campus_branch || campusConfig.campusBranch,
        },
      };
    }
    return base;
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Interview quota ─────────────────────────────────────────────
  const [quota, setQuota] = useState(null);
  const [quotaLoading, setQuotaLoading] = useState(true);
  const [showLiveExitDialog, setShowLiveExitDialog] = useState(false);
  const quotaExhausted = quota !== null && quota.limit > 0 && quota.remaining === 0;

  const refreshQuota = async () => {
    try {
      const res = await api.interview.getQuotaStatus();
      setQuota(res.data.quota);
    } catch {
      // Non-critical — degrade gracefully
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.interview.getQuotaStatus();
        if (!cancelled) setQuota(res.data.quota);
      } catch {
        // Non-critical — degrade gracefully (no banner shown)
      } finally {
        if (!cancelled) setQuotaLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);
  // ───────────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!prefillJobId) return;
      // Only prefill if empty — never overwrite user-entered JD.
      if (interviewConfig.jobDescription && interviewConfig.jobDescription.trim().length > 0) return;
      try {
        const res = await api.hiring.getJob(prefillJobId);
        const jd = res?.data?.description || '';
        if (cancelled) return;
        if (jd && String(jd).trim().length > 0) {
          setInterviewConfig((prev) => {
            if (prev.jobDescription && prev.jobDescription.trim().length > 0) return prev;
            return { ...prev, jobDescription: jd };
          });
          // Nudge user into setup where JD is visible.
          setActiveStep((s) => (s < 2 ? 2 : s));
        }
      } catch {
        // Non-critical — ignore
      }
    };
    run();
    return () => { cancelled = true; };
    // Intentionally exclude interviewConfig to avoid loops; guarded by state updater above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillJobId]);

  const handleNext = () => setActiveStep((s) => Math.min(s + 1, 4));
  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const handleResumeUpload = (data) => {
    setResumeData(data);
    if (data._config) {
      setInterviewConfig((prev) => ({ ...prev, ...data._config }));
    }
    handleNext();
  };

  const handleInterviewSetup = (config) => {
    setInterviewConfig(config);
    setError(null);
    handleNext();
  };

  const handleInterviewComplete = (sessionResults) => {
    setResults(sessionResults);
    handleNext();
    // Re-fetch quota so the results page (and any return to /interview/practice)
    // shows the updated remaining count immediately after the interview ends.
    refreshQuota();
  };

  const handleBackToDashboard = () => {
    if (isAuthenticated) {
      navigate('/');
    } else {
      setActiveStep(0);
      setResumeData(null);
      setResults(null);
    }
  };

  const renderStepContent = (step) => {
    if (step < 0 || step > 4) {
      setActiveStep(0);
      return <Alert severity="warning">Navigation error detected. Returning to start...</Alert>;
    }
    switch (step) {
      case 0:
        return (
          <RoleSelection
            config={interviewConfig}
            onNext={(roleData) => {
              setInterviewConfig((prev) => ({ ...prev, ...roleData }));
              handleNext();
            }}
          />
        );
      case 1:
        return (
          <ResumeUpload
            config={interviewConfig}
            roleData={interviewConfig}
            onUpload={handleResumeUpload}
            loading={loading}
            error={error}
          />
        );
      case 2:
        return (
          <InterviewSetup
            resumeData={resumeData}
            config={interviewConfig}
            onSetup={handleInterviewSetup}
            onBack={handleBack}
            maxDuration={quota?.max_duration_minutes ?? 60}
          />
        );
      case 3:
        return (
          <VoiceInterview
            resumeData={resumeData}
            config={interviewConfig}
            onComplete={handleInterviewComplete}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <InterviewResults
            results={results}
            onRestart={() => {
              setActiveStep(0);
              setResumeData(null);
              setResults(null);
            }}
            onBackToDashboard={isAuthenticated ? handleBackToDashboard : null}
          />
        );
      default:
        return <Alert severity="error">Navigation error. Restarting interview flow...</Alert>;
    }
  };

  const isLiveInterview = activeStep === 3;
  const isResultsStep = activeStep === 4;
  const hideFlowChrome = isLiveInterview; // immersive live mode

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }} data-testid="interview-flow">
      {/* ── Top nav bar — matches app shell style ── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
          color: 'white',
          px: { xs: 2, sm: 4 },
          py: hideFlowChrome ? 1 : 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(13,71,161,0.25)',
        }}
      >
        {/* Brand mark */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: hideFlowChrome ? 32 : 36,
              height: hideFlowChrome ? 32 : 36,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.18)',
              border: '1.5px solid rgba(255,255,255,0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PsychologyIcon sx={{ fontSize: hideFlowChrome ? 18 : 22, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1, color: 'white' }}>
              JobMockAI
            </Typography>
            {!hideFlowChrome && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1 }}>
                Interview Practice
              </Typography>
            )}
            {hideFlowChrome && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1 }}>
                Live interview
              </Typography>
            )}
          </Box>
        </Box>

        {/* Back / deliberate exit — never ambient sidebar chrome during live */}
        {isAuthenticated && !hideFlowChrome && (
          <Button
            variant="outlined-white"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToDashboard}
          >
            Dashboard
          </Button>
        )}
        {hideFlowChrome && (
          <Button
            variant="outlined-white"
            size="small"
            onClick={() => setShowLiveExitDialog(true)}
            data-testid="interview-flow-exit"
            sx={{ textTransform: 'none', borderColor: 'rgba(255,255,255,0.5)', color: 'white' }}
          >
            Exit
          </Button>
        )}
      </Box>

      <Dialog
        open={showLiveExitDialog}
        onClose={() => setShowLiveExitDialog(false)}
        aria-labelledby="live-exit-dialog-title"
      >
        <DialogTitle id="live-exit-dialog-title">Leave interview?</DialogTitle>
        <DialogContent>
          <Typography>
            Your session will stop and you will leave the live interview. To save progress and see
            results, use End interview on the interview screen instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLiveExitDialog(false)} data-testid="interview-flow-exit-stay">
            Stay
          </Button>
          <Button
            color="inherit"
            onClick={() => {
              setShowLiveExitDialog(false);
              if (isAuthenticated) handleBackToDashboard();
              else handleBack();
            }}
            data-testid="interview-flow-exit-confirm"
          >
            Leave
          </Button>
        </DialogActions>
      </Dialog>

      <Container
        maxWidth={hideFlowChrome ? 'md' : 'lg'}
        sx={{ py: { xs: hideFlowChrome ? 2 : 3, sm: hideFlowChrome ? 2.5 : 5 }, px: { xs: 2, sm: 3 } }}
      >
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          {/* Stepper — hidden during live interview for focus */}
          {!hideFlowChrome && (
            <>
              <Box sx={{ px: { xs: 2, sm: 5 }, py: { xs: 2, sm: 3 }, bgcolor: 'background.default' }}>
                <InterviewQuotaBanner quota={quota} loading={quotaLoading} />

                <Stepper
                  activeStep={activeStep}
                  data-testid="interview-flow-stepper"
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: { xs: '0.65rem', sm: '0.8rem' },
                      fontWeight: 500,
                    },
                    '& .MuiStepConnector-line': { display: { xs: 'none', sm: 'block' } },
                    '& .MuiStep-root': { padding: { xs: '0 2px', sm: '0 8px' } },
                  }}
                  orientation="horizontal"
                >
                  {(isMobile ? mobileSteps : steps).map((label, index) => (
                    <Step key={index}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
              <Divider />
            </>
          )}

          {/* Step content */}
          <Box sx={{ px: { xs: 2, sm: hideFlowChrome ? 3 : 5 }, py: { xs: 2.5, sm: hideFlowChrome ? 3 : 4 } }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box
              sx={{ minHeight: hideFlowChrome ? 320 : '400px' }}
              data-testid="interview-flow-content"
              data-live={hideFlowChrome ? 'true' : 'false'}
              style={{ pointerEvents: quotaExhausted ? 'none' : 'auto', opacity: quotaExhausted ? 0.45 : 1 }}
            >
              <Suspense fallback={<PageLoader />}>{renderStepContent(activeStep)}</Suspense>
            </Box>

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        </Paper>

        {!hideFlowChrome && !isResultsStep && (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.disabled">
              Powered by AI &nbsp;·&nbsp; Voice Recognition &nbsp;·&nbsp; Real-time Feedback
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════════
// Main Router — defines all application routes
// ═════════════════════════════════════════════════════════════════
const AppRoutes = () => {
  const { loading: authLoading } = useAuth();

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <main id="app-main" role="main">
      <HashRedirect />
      <Routes>
        {/* ── Public routes (no AppShell) ───────────────────────── */}
        {/* Root URL → /landing for guests (canonical marketing homepage) */}
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/landing" element={<LandingWrapper />} />
        <Route
          path="/candidate"
          element={
            <Suspense fallback={<PageLoader />}>
              <CandidatePage />
            </Suspense>
          }
        />
        <Route
          path="/product-tours"
          element={
            <Suspense fallback={<PageLoader />}>
              <ProductToursPage />
            </Suspense>
          }
        />
        <Route
          path="/pricing"
          element={
            <Suspense fallback={<PageLoader />}>
              <PublicPricingPage />
            </Suspense>
          }
        />
        <Route
          path="/about"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingStaticPage Page={AboutPage} />
            </Suspense>
          }
        />
        <Route
          path="/careers"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingStaticPage Page={CareersPage} />
            </Suspense>
          }
        />
        <Route
          path="/features"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingStaticPage Page={FeaturesPage} />
            </Suspense>
          }
        />
        <Route
          path="/developers"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingStaticPage Page={ApiOverviewPage} />
            </Suspense>
          }
        />
        <Route
          path="/integrations"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingStaticPage Page={IntegrationsPage} />
            </Suspense>
          }
        />
        <Route
          path="/help"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingStaticPage Page={HelpCenterPage} />
            </Suspense>
          }
        />
        <Route
          path="/contact"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingStaticPage Page={ContactUsPage} />
            </Suspense>
          }
        />
        <Route
          path="/status"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingStaticPage Page={StatusPage} />
            </Suspense>
          }
        />
        <Route
          path="/community"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingStaticPage Page={CommunityMarketingPage} />
            </Suspense>
          }
        />
        <Route
          path="/cookies"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingStaticPage Page={CookiePolicyPage} />
            </Suspense>
          }
        />
        <Route
          path="/gdpr"
          element={
            <Suspense fallback={<PageLoader />}>
              <MarketingStaticPage Page={GdprPage} />
            </Suspense>
          }
        />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/register" element={<AuthPage />} />
        <Route path="/signup" element={<AuthPage />} />
        <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
        <Route path="/auth/callback" element={<OAuthCallbackPage />} />
        <Route path="/forgot-password" element={<Suspense fallback={<PageLoader />}><ForgotPassword /></Suspense>} />
        <Route path="/reset-password/:token" element={<Suspense fallback={<PageLoader />}><ResetPassword /></Suspense>} />
        <Route path="/reset-password" element={<Suspense fallback={<PageLoader />}><ResetPassword /></Suspense>} />
        <Route path="/verify-email/:token" element={<Suspense fallback={<PageLoader />}><VerifyEmail /></Suspense>} />
        <Route path="/email-verified" element={<Suspense fallback={<PageLoader />}><VerifyEmail /></Suspense>} />
        <Route path="/payment-return" element={<Suspense fallback={<PageLoader />}><PaymentReturn /></Suspense>} />
        <Route path="/payment-success" element={<Navigate to="/payment-return?session_id=success" replace />} />
        <Route path="/payment-cancel" element={<Navigate to="/payment-return?canceled=true" replace />} />
        <Route path="/shared/:shareToken" element={<Suspense fallback={<PageLoader />}><SharedInterview /></Suspense>} />
        <Route path="/terms" element={<Suspense fallback={<PageLoader />}><PublicLegalPage type="TermsOfService" /></Suspense>} />
        <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PublicLegalPage type="PrivacyPolicy" /></Suspense>} />
        <Route path="/demo" element={<DemoBookingRedirect />} />

        <Route
          path="/blog"
          element={
            <Suspense fallback={<PageLoader />}>
              <ContentListPage
                category="blog"
                title="Blog"
                description="Tips for interviews, resume strategy, and practice ideas from JobMockAI."
                basePath="/blog"
              />
            </Suspense>
          }
        />
        <Route
          path="/blog/:slug"
          element={
            <Suspense fallback={<PageLoader />}>
              <ContentDetailPage expectedCategory="blog" basePath="/blog" />
            </Suspense>
          }
        />
        <Route
          path="/interview-questions"
          element={
            <Suspense fallback={<PageLoader />}>
              <InterviewQuestionsHubPage />
            </Suspense>
          }
        />
        <Route
          path="/interview-questions/:slug"
          element={
            <Suspense fallback={<PageLoader />}>
              <ContentDetailPage expectedCategory="interview_question" basePath="/interview-questions" />
            </Suspense>
          }
        />
        <Route
          path="/guides"
          element={
            <Suspense fallback={<PageLoader />}>
              <ContentListPage
                category="guide"
                title="Guides"
                description="Step-by-step guides to answer common interview prompts with confidence."
                basePath="/guides"
              />
            </Suspense>
          }
        />
        <Route
          path="/guides/:slug"
          element={
            <Suspense fallback={<PageLoader />}>
              <ContentDetailPage expectedCategory="guide" basePath="/guides" />
            </Suspense>
          }
        />

        {/* ── Interview flow (standalone shell — no AppShell double chrome) ── */}
        <Route
          path="/interview/practice"
          element={
            <Suspense fallback={<PageLoader />}>
              <InterviewFlow />
            </Suspense>
          }
        />
        <Route
          path="/interview/start"
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <InterviewFlow />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* Guest applicants open claim links before/after signup */}
        <Route
          path="/hiring/claim-application/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <ClaimApplicationPage />
            </Suspense>
          }
        />

        {/* ── Public job apply (guest-friendly URL for sharing) ─── */}
        <Route
          path="/apply/:id"
          element={
            <Suspense fallback={<PageLoader />}>
              <JobDetail variant="public" />
            </Suspense>
          }
        />

        {/* ── SEO-friendly job detail: /jobs/:companySlug/:jobSlug ── */}
        <Route
          path="/jobs/:companySlug/:jobSlug"
          element={
            <Suspense fallback={<PageLoader />}>
              <JobDetail variant="public" />
            </Suspense>
          }
        />

        {/* ── Public: become a founding interviewer (no auth) ──── */}
        <Route
          path="/become-an-interviewer"
          element={
            <Suspense fallback={<PageLoader />}>
              <BecomeInterviewerPage />
            </Suspense>
          }
        />

        {/* ── Public job discovery (/jobs) — shell only when logged in ─ */}
        <Route element={<JobsDiscoveryLayout />}>
          <Route
            path="/jobs"
            element={
              <Suspense fallback={<PageLoader />}>
                <PublicJobBoard />
              </Suspense>
            }
          />
          <Route
            path="/interview-prep/:jobId"
            element={
              <Suspense fallback={<PageLoader />}>
                <InterviewPrepHub />
              </Suspense>
            }
          />
        </Route>

        {/* ── Authenticated routes inside AppShell ──────────────── */}
        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          {/* Dashboard — use /dashboard only; do not add index here (index would steal / from HomeRedirect) */}
          <Route path="consent" element={<Suspense fallback={<PageLoader />}><ConsentPage /></Suspense>} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="dashboard/recordings" element={<Suspense fallback={<PageLoader />}><LocalRecordingsPage /></Suspense>} />
          <Route path="progress" element={<Suspense fallback={<PageLoader />}><ProgressDashboard /></Suspense>} />
          <Route path="career-plan" element={<Suspense fallback={<PageLoader />}><CareerGrowth /></Suspense>} />

          {/* Practice — /interview/start is standalone (outside AppShell) */}
          <Route path="interview/history" element={<Suspense fallback={<PageLoader />}><InterviewHistory /></Suspense>} />
          <Route path="practice/interviews" element={<Suspense fallback={<PageLoader />}><PracticeInterviewsPage /></Suspense>} />
          <Route path="practice/interviews/share-experience" element={<Suspense fallback={<PageLoader />}><ShareInterviewExperiencePage /></Suspense>} />
          <Route path="practice/interviews/:templateId" element={<Suspense fallback={<PageLoader />}><PracticeInterviewDetailPage /></Suspense>} />
          <Route path="campus/placement" element={<Suspense fallback={<PageLoader />}><PlacementPrepHubPage /></Suspense>} />
          <Route path="campus/placement/aptitude" element={<Suspense fallback={<PageLoader />}><AptitudeQuizPage /></Suspense>} />
          <Route path="campus/placement/drill" element={<Suspense fallback={<PageLoader />}><CampusDrillPage /></Suspense>} />
          <Route path="campus/tpo" element={<Suspense fallback={<PageLoader />}><CampusTpoPage /></Suspense>} />
          <Route path="replay/:sessionId" element={<ReplayPage />} />
          <Route path="replay" element={<Suspense fallback={<PageLoader />}><Box sx={{ p: 2 }}><Typography variant="h5">Interview Replay</Typography><Typography color="text.secondary">Select a session from Interview History to replay</Typography></Box></Suspense>} />

          {/* Community */}
          <Route path="community/discover" element={<Suspense fallback={<PageLoader />}><PeopleDiscovery /></Suspense>} />
          <Route path="community/feed" element={<Suspense fallback={<PageLoader />}><CommunityFeed /></Suspense>} />
          <Route path="community/connections" element={<Suspense fallback={<PageLoader />}><MyConnections /></Suspense>} />
          <Route path="community/messages" element={<Suspense fallback={<PageLoader />}><CommunityMessages /></Suspense>} />
          <Route path="community/messages/:threadId" element={<Suspense fallback={<PageLoader />}><CommunityMessages /></Suspense>} />
          <Route path="community/profile/edit" element={<Suspense fallback={<PageLoader />}><EditCommunityProfile /></Suspense>} />
          <Route path="community/profile/:userId" element={<Suspense fallback={<PageLoader />}><PublicProfile /></Suspense>} />
          <Route path="rooms" element={<Suspense fallback={<PageLoader />}><InterviewRoom /></Suspense>} />
          <Route path="rooms/:roomId/lobby" element={<Suspense fallback={<PageLoader />}><RoomLobby /></Suspense>} />
          <Route path="rooms/join/:roomCode" element={<Suspense fallback={<PageLoader />}><RoomJoin /></Suspense>} />
          <Route path="rooms/:roomId" element={<Suspense fallback={<PageLoader />}><InterviewRoom /></Suspense>} />
          <Route path="schedules" element={<MySchedulesPage />} />
          <Route path="schedule/:connectionId" element={<ScheduleInterviewPage />} />

          {/* Learn */}
          <Route path="learning-hub" element={<Suspense fallback={<PageLoader />}><LearningHub /></Suspense>} />
          <Route path="learning-hub/path/:pathId" element={<Suspense fallback={<PageLoader />}><LearningPath /></Suspense>} />
          <Route path="learning-hub/resource/:resourceId" element={<Suspense fallback={<PageLoader />}><LearningResource /></Suspense>} />
          <Route path="coach" element={<Suspense fallback={<PageLoader />}><AICoach /></Suspense>} />
          <Route path="agents" element={<Suspense fallback={<PageLoader />}><AgentsHubPage /></Suspense>} />
          <Route path="agents/new" element={<Suspense fallback={<PageLoader />}><AgentWizardPage /></Suspense>} />
          <Route path="agents/:id/onboarding" element={<Suspense fallback={<PageLoader />}><AgentOnboardingPage /></Suspense>} />
          <Route path="agents/:id/readiness" element={<Suspense fallback={<PageLoader />}><ReadinessReportPage /></Suspense>} />
          <Route path="agents/:id/chat" element={<Suspense fallback={<PageLoader />}><AgentChatPage /></Suspense>} />
          <Route path="agents/:id" element={<Suspense fallback={<PageLoader />}><AgentMissionDashboard /></Suspense>} />

          {/* Compete */}
          <Route path="leaderboard" element={<Suspense fallback={<PageLoader />}><Leaderboard /></Suspense>} />
          <Route path="games" element={<Suspense fallback={<PageLoader />}><GamesHubPage /></Suspense>} />
          <Route path="games/sudoku" element={<Suspense fallback={<PageLoader />}><DailySudokuPage /></Suspense>} />
          <Route path="games/queens" element={<Suspense fallback={<PageLoader />}><QueensGamePage /></Suspense>} />
          <Route path="games/binary" element={<Suspense fallback={<PageLoader />}><BinaryGridGamePage /></Suspense>} />
          <Route path="games/daily" element={<Suspense fallback={<PageLoader />}><DailyPuzzlePage /></Suspense>} />
          <Route path="achievements" element={<Suspense fallback={<PageLoader />}><Achievements /></Suspense>} />
          <Route path="xp" element={<Suspense fallback={<PageLoader />}><XPHistory /></Suspense>} />

          {/* Jobs */}
          <Route path="browse-jobs" element={<Suspense fallback={<PageLoader />}><JobBoard primaryNav /></Suspense>} />
          <Route path="job-board" element={<Suspense fallback={<PageLoader />}><JobBoard primaryNav /></Suspense>} />
          <Route path="jobs/company/:slug" element={<Suspense fallback={<PageLoader />}><CompanyPublicPage /></Suspense>} />
          <Route path="invite/company" element={<Suspense fallback={<PageLoader />}><InviteAcceptPage /></Suspense>} />
          <Route path="jobs/:id" element={<Suspense fallback={<PageLoader />}><JobDetail /></Suspense>} />
          <Route path="my-applications" element={<Suspense fallback={<PageLoader />}><MyApplications /></Suspense>} />
          <Route path="hiring/settings" element={<Suspense fallback={<PageLoader />}><CandidateVisibility /></Suspense>} />
          <Route path="hiring/interview-templates" element={<Suspense fallback={<PageLoader />}><InterviewTemplateBuilderPage /></Suspense>} />
          <Route path="hiring/ai-assignments/:assignmentId" element={<Suspense fallback={<PageLoader />}><AssignedAiInterviewPage /></Suspense>} />
          <Route path="hiring/booking-prep/:bookingId" element={<Suspense fallback={<PageLoader />}><BookingPrepInterviewPage /></Suspense>} />
          <Route path="hiring/*" element={<Suspense fallback={<PageLoader />}><HiringMarketplace /></Suspense>} />

          {/* Resume Management */}
          <Route path="resumes" element={<Suspense fallback={<PageLoader />}><ResumeManager /></Suspense>} />
          <Route path="resumes/analyzer" element={<Suspense fallback={<PageLoader />}><ResumeAnalyzer /></Suspense>} />

          {/* Marketplace */}
          <Route path="marketplace" element={<Suspense fallback={<PageLoader />}><MarketplacePage /></Suspense>} />
          <Route path="marketplace/bookings" element={<Suspense fallback={<PageLoader />}><MarketplaceBookings /></Suspense>} />
          <Route path="marketplace/service/:serviceId" element={<Suspense fallback={<PageLoader />}><ServiceDetailPage /></Suspense>} />
          <Route path="marketplace/provider/:providerId" element={<Suspense fallback={<PageLoader />}><ProviderProfilePage /></Suspense>} />

          {/* Interviewer Dashboard (self-service) */}
          <Route path="interviewer/dashboard" element={<Suspense fallback={<PageLoader />}><InterviewerDashboard /></Suspense>} />

          {/* Apply to become an interviewer */}
          <Route path="marketplace/apply" element={<Suspense fallback={<PageLoader />}><ApplyInterviewer /></Suspense>} />

          {/* Account */}
          <Route path="profile" element={<Suspense fallback={<PageLoader />}><UserProfile /></Suspense>} />
          <Route path="settings" element={<Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>} />
          <Route path="billing" element={<Suspense fallback={<PageLoader />}><BillingPage /></Suspense>} />
          <Route path="referrals" element={<Suspense fallback={<PageLoader />}><ReferralDashboard /></Suspense>} />
          <Route path="rewards" element={<Suspense fallback={<PageLoader />}><RewardsStore /></Suspense>} />
          <Route path="notifications" element={<Suspense fallback={<PageLoader />}><NotificationsPage /></Suspense>} />

          {/* AI Observations */}
          <Route path="ai-observations" element={<MyObservationsPage />} />
          <Route path="observation-report/:roomId" element={<ObservationReportPage />} />

          {/* Misc authenticated */}
          <Route path="reviews" element={<Suspense fallback={<PageLoader />}><ReviewsPage /></Suspense>} />
          <Route path="reviews/legacy" element={<Suspense fallback={<PageLoader />}><Reviews /></Suspense>} />
          <Route path="feedback" element={<Suspense fallback={<PageLoader />}><FeedbackPage /></Suspense>} />
          <Route path="pricing" element={<Navigate to="/pricing" replace />} />
          <Route path="enterprise-insights" element={<Suspense fallback={<PageLoader />}><EnterpriseCandidateInsights /></Suspense>} />
          <Route path="interview/custom-builder" element={<Suspense fallback={<PageLoader />}><CustomInterviewBuilderPage /></Suspense>} />
          <Route path="voice-coach" element={<Suspense fallback={<PageLoader />}><VoiceCoachPage /></Suspense>} />
          <Route path="tts-debug" element={<Suspense fallback={<PageLoader />}><TTSDebugger /></Suspense>} />

          {/* Demo Video Studio — admin / marketing (REACT_APP_ENABLE_DEMO_STUDIO) */}
          {isDemoStudioEnabled() && (
            <Route
              path="studio/demo"
              element={
                <DemoStudioRoute>
                  <Suspense fallback={<PageLoader />}>
                    <DemoVideoStudio />
                  </Suspense>
                </DemoStudioRoute>
              }
            />
          )}

          {/* Admin */}
          <Route
            path="admin"
            element={
              <AdminRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminLayout />
                </Suspense>
              </AdminRoute>
            }
          >
            <Route index element={<Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense>} />
            <Route path="users" element={<Suspense fallback={<PageLoader />}><UserManagement /></Suspense>} />
            <Route path="feature-flags" element={<Suspense fallback={<PageLoader />}><FeatureFlagManager /></Suspense>} />
            <Route path="plans" element={<Suspense fallback={<PageLoader />}><PlanQuotasManager /></Suspense>} />
            <Route path="audit-logs" element={<Suspense fallback={<PageLoader />}><AuditLogViewer /></Suspense>} />
            <Route path="ai-usage" element={<Suspense fallback={<PageLoader />}><AIUsageDashboard /></Suspense>} />
            <Route path="companies" element={<Suspense fallback={<PageLoader />}><CompanyManagement /></Suspense>} />
            <Route path="job-moderation" element={<Suspense fallback={<PageLoader />}><AdminJobModerationPage /></Suspense>} />
            <Route path="practice-interview-moderation" element={<Suspense fallback={<PageLoader />}><AdminPracticeInterviewModerationPage /></Suspense>} />
            <Route path="study-catalog" element={<Suspense fallback={<PageLoader />}><AdminStudyCatalogPage /></Suspense>} />
            <Route path="interviewers" element={<Suspense fallback={<PageLoader />}><AdminInterviewerPage /></Suspense>} />
            <Route
              path="interviewer-applications"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminInterviewerApplicationsPage />
                </Suspense>
              }
            />
            <Route
              path="founding-interviewer-applications"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminFoundingInterviewerApplicationsPage />
                </Suspense>
              }
            />
            <Route
              path="listing-reviews"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminListingReviewPage />
                </Suspense>
              }
            />
            <Route
              path="media-moderation"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminMediaModerationPage />
                </Suspense>
              }
            />
            <Route
              path="video-limits"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminVideoLimitsPage />
                </Suspense>
              }
            />
            <Route
              path="payouts"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AdminPayoutManagementPage />
                </Suspense>
              }
            />
            <Route path="legal" element={<Suspense fallback={<PageLoader />}><AdminLegalPage /></Suspense>} />
          </Route>
        </Route>

        {/* ── Catch-all: redirect to landing or dashboard ──────── */}
        <Route path="*" element={<CatchAllRedirect />} />
      </Routes>
      <PWAInstallPrompt />
    </main>
  );
};

/** Redirect unknown paths: authenticated → /dashboard, guest → /landing */
const CatchAllRedirect = () => {
  const { isAuthenticated } = useAuth();
  return <Navigate to={isAuthenticated ? '/dashboard' : '/landing'} replace />;
};

// ═════════════════════════════════════════════════════════════════
// App — Root component
// ═════════════════════════════════════════════════════════════════
const App = () => {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;